import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, TEACHER_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ASSIGNMENT_KINDS, eduRpc, GRADE_LEVELS, SUBJECTS } from "@/lib/education";
import { AiControls } from "@/components/AiControls";
import { DEFAULT_AI_POLICY, type AiPolicy } from "@/lib/ai-policy";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/assignments/new")({
  validateSearch: (search: Record<string, unknown>) => ({
    classId: typeof search.classId === "string" ? search.classId : undefined,
  }),
  component: NewAssignment,
  head: () => ({
    meta: [
      { title: "New assignment - Inquira teacher" },
      { name: "description", content: "Build an assignment with instructions, questions, due date, points and AI rules." },
      { property: "og:title", content: "Create an Inquira assignment" },
      { property: "og:description", content: "Set instructions, questions, deadlines and how much AI help is allowed." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface QuestionDraft { prompt: string; answer: string; points: number }

function NewAssignment() {
  const { classId } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classes, setClasses] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [policy, setPolicy] = useState<AiPolicy>({ ...DEFAULT_AI_POLICY });
  const [form, setForm] = useState({
    class_id: classId ?? "",
    title: "",
    instructions: "",
    kind: "homework",
    subject: "",
    grade_level: "",
    due_at: "",
    points: 100,
    ai_mode: "guided",
    allow_late: true,
  });

  useEffect(() => {
    if (!user) return;
    void supabase
      .from("classes")
      .select("id, name")
      .eq("teacher_id", user.id)
      .eq("archived", false)
      .order("name")
      .then(({ data }) => {
        const list = (data as Array<{ id: string; name: string }>) ?? [];
        setClasses(list);
        setForm((f) => ({ ...f, class_id: f.class_id || (list[0]?.id ?? "") }));
      });
  }, [user]);

  async function save(status: "draft" | "published") {
    if (!form.class_id) { toast.error(i18n.t("edu.teacher_assignments_new.pick_a_class")); return; }
    if (!form.title.trim()) { toast.error(i18n.t("edu.teacher_assignments_new.give_the_assignment_a_title")); return; }
    setSaving(true);
    const { data, error } = await eduRpc<string>("edu_save_assignment", {
      _class_id: form.class_id,
      _title: form.title,
      _instructions: form.instructions || null,
      _kind: form.kind,
      _subject: form.subject || null,
      _grade_level: form.grade_level || null,
      _due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
      _points: form.points,
      _ai_mode: policy.mode === "full" ? "full" : policy.mode === "disabled" ? "disabled" : "guided",
      _allow_late: form.allow_late,
      _status: status,
      _questions: questions.filter((q) => q.prompt.trim()),
    });
    setSaving(false);
    if (error || !data) { toast.error(error ?? "Could not save"); return; }
    const { error: policyError } = await eduRpc("edu_set_assignment_ai_policy", {
      _assignment_id: data,
      _policy: policy,
    });
    if (policyError) toast.error(`Assignment saved, but the AI rules did not: ${policyError}`);
    toast.success(status === "published" ? "Assignment published" : "Draft saved");
    void navigate({ to: "/teacher/assignments/$assignmentId", params: { assignmentId: data } });
  }

  return (
    <EduShell nav={TEACHER_NAV} title={i18n.t("edu.teacher_assignments_new.new_assignment")} subtitle={i18n.t("edu.teacher_assignments_new.save_as_a_draft_first_or_publish_straigh")}>
      <div className="space-y-4 rounded-2xl border border-rule bg-card p-4">
        <select
          className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
          value={form.class_id}
          onChange={(e) => setForm({ ...form, class_id: e.target.value })}
        >
          <option value=""><T k="edu.teacher_assignments_new.choose_a_class" /></option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input
          className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
          placeholder={i18n.t("edu.teacher_assignments_new.title")}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <textarea
          className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
          rows={4}
          placeholder={i18n.t("edu.teacher_assignments_new.instructions_for_students")}
          value={form.instructions}
          onChange={(e) => setForm({ ...form, instructions: e.target.value })}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
          >
            {ASSIGNMENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <select
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
          >
            <option value=""><T k="edu.teacher_assignments_new.subject" /></option>
            {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.grade_level}
            onChange={(e) => setForm({ ...form, grade_level: e.target.value })}
          >
            <option value=""><T k="edu.teacher_assignments_new.level" /></option>
            {GRADE_LEVELS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <input
            type="datetime-local"
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.due_at}
            onChange={(e) => setForm({ ...form, due_at: e.target.value })}
          />
          <input
            type="number"
            min={0}
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.points}
            onChange={(e) => setForm({ ...form, points: Number(e.target.value) })}
          />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.allow_late}
              onChange={(e) => setForm({ ...form, allow_late: e.target.checked })}
            />
            Accept late work
          </label>
        </div>

        <AiControls value={policy} onChange={setPolicy} />


        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted"><T k="edu.teacher_assignments_new.questions_optional" /></p>
            <button
              className="text-xs font-semibold text-neon-pink"
              onClick={() => setQuestions([...questions, { prompt: "", answer: "", points: 1 }])}
            >
              Add question
            </button>
          </div>
          <div className="space-y-3">
            {questions.map((q, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-rule p-3">
                <textarea
                  className="w-full rounded-lg border border-rule bg-background px-3 py-2 text-sm text-ink"
                  rows={2}
                  placeholder={`Question ${i + 1}`}
                  value={q.prompt}
                  onChange={(e) => {
                    const next = [...questions];
                    next[i] = { ...q, prompt: e.target.value };
                    setQuestions(next);
                  }}
                />
                <div className="flex gap-2">
                  <input
                    className="flex-1 rounded-lg border border-rule bg-background px-3 py-2 text-sm text-ink"
                    placeholder={i18n.t("edu.teacher_assignments_new.answer_key_only_you_see_this")}
                    value={q.answer}
                    onChange={(e) => {
                      const next = [...questions];
                      next[i] = { ...q, answer: e.target.value };
                      setQuestions(next);
                    }}
                  />
                  <input
                    type="number"
                    min={0}
                    className="w-20 rounded-lg border border-rule bg-background px-3 py-2 text-sm text-ink"
                    value={q.points}
                    onChange={(e) => {
                      const next = [...questions];
                      next[i] = { ...q, points: Number(e.target.value) };
                      setQuestions(next);
                    }}
                  />
                  <button
                    className="text-xs font-semibold text-destructive"
                    onClick={() => setQuestions(questions.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={() => save("published")} disabled={saving}><T k="edu.teacher_assignments_new.publish" /></Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving}><T k="edu.teacher_assignments_new.save_draft" /></Button>
        </div>
      </div>
    </EduShell>
  );
}
